import type { TFunction } from 'i18next';

import type {
  RootCauseResult,
  RootCausesData,
} from 'src/types/followUpStatistics';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';

export interface RootCauseBarView {
  label: string;
  count: number;
  percentage: number;
  percentageLabel: string;
  pillLabel: string;
  tooltip: string;
}

export interface RootCauseData {
  bars: RootCauseBarView[];
  total: number;
}

const getResultCount = (result: RootCauseResult) => {
  if (typeof result.count === 'number') return result.count;
  if (typeof result.value === 'number') return result.value;
  return 0;
};

const formatNumber = (value: number, locale: string) =>
  Number.isFinite(value) ? value.toLocaleString(locale) : '0';

const formatPercentLabel = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(value);

export const buildRootCauseData = ({
  data,
  locale,
  t,
}: {
  data?: RootCausesData | null;
  locale: string;
  t: TFunction;
}): RootCauseData => {
  if (!data) {
    return {
      bars: [],
      total: 0,
    };
  }

  const total = data.results.reduce(
    (sum, result) => sum + getResultCount(result),
    0,
  );

  const sortedResults = [...data.results].sort(
    (a, b) => getResultCount(b) - getResultCount(a),
  );

  const bars: RootCauseBarView[] = sortedResults.map((result) => {
    const count = getResultCount(result);
    const percentage = getRelativeValue(count, total);
    const percentageLabel = formatPercentLabel(percentage, locale);

    return {
      label: result.label,
      count,
      percentage,
      percentageLabel,
      pillLabel: `${percentageLabel}%`,
      tooltip: t('statistics.rootCausesWidget.tooltip', {
        countLabel: formatNumber(count, locale),
        percentOfAnswered: percentageLabel,
        defaultValue: '{{countLabel}} Follow-Ups ({{percentOfAnswered}}%)',
      }),
    };
  });

  return {
    bars,
    total,
  };
};
